import { createServerFn } from "@tanstack/react-start";

import type {
  InvitationAcceptResponse,
  InvitationBatchCreateResponse,
  InvitationDetailResponse,
  InvitationListResponse,
  InvitationPublicDetailResponse,
} from "@/lib/api/generated";
import {
  acceptInvitation,
  acceptInvitationAndJoin,
  batchCreateWorkspaceInvitations,
  createWorkspaceInvitation,
  declineInvitation,
  getInvitationByToken,
  listWorkspaceInvitations,
  revokeWorkspaceInvitation,
} from "@/lib/api/generated/workspace-invitations/workspace-invitations";
import { logError, logInfo } from "@/lib/structured-log";

type WorkspaceRoleType = "workspace_owner" | "workspace_member";

/**
 * 邀请单个成员
 */
export const createInvitationServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; email: string; message?: string; role?: WorkspaceRoleType }) =>
      data,
  )
  .handler(async ({ data }): Promise<InvitationDetailResponse> => {
    try {
      const response = await createWorkspaceInvitation(data.workspaceId, {
        email: data.email,
        message: data.message,
        role: data.role ?? "workspace_member",
      });

      if (response.status !== 201) {
        logError("workspace.invitations.create.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          email: data.email,
        });
        throw new Error(`Failed to create invitation: ${response.status}`);
      }

      logInfo("workspace.invitations.create.success", {
        workspace_id: data.workspaceId,
        email: data.email,
        invitation_id: response.data.id,
      });
      return response.data;
    } catch (error) {
      logError("workspace.invitations.create.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        email: data.email,
      });
      throw error;
    }
  });

/**
 * 批量邀请成员
 */
export const batchInviteServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; emails: string[]; role?: WorkspaceRoleType }) => data)
  .handler(async ({ data }): Promise<InvitationBatchCreateResponse> => {
    try {
      const response = await batchCreateWorkspaceInvitations(data.workspaceId, {
        emails: data.emails,
        role: data.role ?? "workspace_member",
      });

      if (response.status !== 200) {
        logError("workspace.invitations.batch.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          email_count: data.emails.length,
        });
        throw new Error(`Failed to batch invite: ${response.status}`);
      }

      logInfo("workspace.invitations.batch.success", {
        workspace_id: data.workspaceId,
        succeeded: response.data.total_succeeded,
        failed: response.data.total_failed,
      });
      return response.data;
    } catch (error) {
      logError("workspace.invitations.batch.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 列出待处理邀请
 */
export const listInvitationsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<InvitationListResponse> => {
    try {
      const response = await listWorkspaceInvitations(data.workspaceId);

      if (response.status !== 200) {
        logError("workspace.invitations.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to list invitations: ${response.status}`);
      }

      logInfo("workspace.invitations.list.success", {
        workspace_id: data.workspaceId,
        count: response.data.total,
      });
      return response.data;
    } catch (error) {
      logError("workspace.invitations.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 撤销邀请
 */
export const revokeInvitationServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; invitationId: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    try {
      const response = await revokeWorkspaceInvitation(data.workspaceId, data.invitationId);

      if (response.status !== 204) {
        logError("workspace.invitations.revoke.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          invitation_id: data.invitationId,
        });
        throw new Error(`Failed to revoke invitation: ${response.status}`);
      }

      logInfo("workspace.invitations.revoke.success", {
        workspace_id: data.workspaceId,
        invitation_id: data.invitationId,
      });
    } catch (error) {
      logError("workspace.invitations.revoke.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        invitation_id: data.invitationId,
      });
      throw error;
    }
  });

/**
 * 获取邀请详情（公开，无需认证）
 */
export const getInvitationByTokenServer = createServerFn({ method: "GET" })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<InvitationPublicDetailResponse> => {
    try {
      const response = await getInvitationByToken(data.token);

      if (response.status !== 200) {
        logError("invitations.get.failed", {
          status: response.status,
        });
        throw new Error(`Failed to get invitation: ${response.status}`);
      }

      logInfo("invitations.get.success", {
        invitation_id: response.data.id,
      });
      return response.data;
    } catch (error) {
      logError("invitations.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

/**
 * 接受邀请
 */
export const acceptInvitationServer = createServerFn({ method: "POST" })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<InvitationAcceptResponse> => {
    try {
      const response = await acceptInvitation(data.token);

      if (response.status !== 200) {
        logError("invitations.accept.failed", {
          status: response.status,
        });
        throw new Error(`Failed to accept invitation: ${response.status}`);
      }

      logInfo("invitations.accept.success", {
        workspace_id: response.data.workspace_id,
      });
      return response.data;
    } catch (error) {
      logError("invitations.accept.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

/**
 * 拒绝邀请
 */
export const declineInvitationServer = createServerFn({ method: "POST" })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    try {
      const response = await declineInvitation(data.token);

      if (response.status !== 204) {
        logError("invitations.decline.failed", {
          status: response.status,
        });
        throw new Error(`Failed to decline invitation: ${response.status}`);
      }

      logInfo("invitations.decline.success", {});
    } catch (error) {
      logError("invitations.decline.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

/**
 * 接受邀请并完成 onboarding（用于新用户通过邀请链接注册的场景）
 *
 * 与普通 acceptInvitationServer 的区别：
 * - 自动将用户 onboarding_state 更新为 WORKSPACE_CREATED
 * - 适用于新用户首次通过邀请链接加入 workspace
 */
export const acceptInvitationAndJoinServer = createServerFn({ method: "POST" })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<InvitationAcceptResponse> => {
    try {
      const response = await acceptInvitationAndJoin(data.token);

      if (response.status !== 200) {
        logError("invitations.accept_and_join.failed", {
          status: response.status,
        });
        throw new Error(`Failed to accept invitation and join: ${response.status}`);
      }

      logInfo("invitations.accept_and_join.success", {
        workspace_id: response.data.workspace_id,
        workspace_slug: response.data.workspace_slug,
      });
      return response.data;
    } catch (error) {
      logError("invitations.accept_and_join.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });
